

import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';


const DEFAULT_PROXY_PATH = (() => {
  // Server-side render fallback
  if (typeof window === 'undefined') {
    return '/api/fmcsa.php';
  }
  const { origin, port } = window.location;
  const isLocal = /https?:\/\/localhost(?::\d+)?/i.test(origin);
  // In local dev, point to hosted proxy explicitly
  if (isLocal && port === '3000') {
    return 'https://test.bmlogisticsllc.com/api/fmcsa.php';
  }
  // In production (same host), hit the bundled PHP proxy
  return '/api/fmcsa.php';
})();

const PROXY_PATH = process.env.REACT_APP_FMCSA_PROXY || DEFAULT_PROXY_PATH;

const DS = {
  BASIC: 'az4n-8mr2.json',        
  CARRIER_INFO: '6eyk-hxee.json',  
  INSURANCE: 'ypjt-5ydn.json',     
  PENDING_INS: 'qh9u-swkp.json',  
  AUTH_HISTORY: '9mw4-x3tu.json',  
  REVOCATIONS: 'sa6p-acbp.json',  
};


const tidy = (s) => (s || '').toString().trim();
const digits = (s) => (s || '').toString().replace(/\D/g, '');
const stripLeadingZeros = (s) => (s || '').toString().replace(/^0+/, '');
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

function normalizeMC(mc) {
  const d = stripLeadingZeros(digits(mc));
  return d ? `MC${d}` : null;
}


function buildProxyUrl(endpoint, params = {}) {
  const u = new URL(PROXY_PATH, window.location.origin);
  u.searchParams.set('endpoint', endpoint);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      u.searchParams.append(k, v);
    }
  });
  return u.toString();
}

async function getJSONViaProxy(endpoint, params) {
  const url = buildProxyUrl(endpoint, params);
  const res = await fetch(url, { method: 'GET', credentials: 'same-origin' });
  if (!res.ok) {
    let msg = `API Error: ${res.status} - ${res.statusText}`;
    try {
      const j = await res.json();
      if (j && j.error) msg += ` | ${j.error}`;
    } catch {/* ignore */}
    throw new Error(msg);
  }
  return res.json();
}


class CarrierAPIService {
  constructor() {
   
  }

 
  detectSearchType(input) {
    const trimmed = tidy(input);

    if (/^MC-?\d{6,8}$/i.test(trimmed) || /^\d{6,8}$/.test(trimmed)) return 'mc';
    if (/^\d{7,8}$/.test(trimmed)) return 'dot';
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'email';
    if (/^\d+\s*-\s*\d+$/.test(trimmed)) return 'range';
    if (/^[a-zA-Z\s.,&]+$/.test(trimmed) && trimmed.length > 2) return 'name';

    return 'auto';
  }

  determineSearchType(text) {
    const trimmedText = tidy(text).toLowerCase();

    const mcRegex = /\b(?:mc|motor carrier|docket|authority)\s*[-:#]?\s*(\d+)/i;
    const dotRegex = /\b(?:dot|usdot)(?:\s*[-:#]?\s*|\s+number\s*[-:#]?\s*)(\d+)/i;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
    const rangeRegex = /^(\d+)\s*-\s*(\d+)$/;

    const rangeMatch = trimmedText.match(rangeRegex);
    if (rangeMatch) return { type: 'range', start: rangeMatch[1], end: rangeMatch[2] };

    if (emailRegex.test(trimmedText)) return { type: 'email', value: trimmedText };

    const mcMatch = trimmedText.match(mcRegex);
    if (mcMatch) return { type: 'mc', value: mcMatch[1] };

    const dotMatch = trimmedText.match(dotRegex);
    if (dotMatch) return { type: 'dot', value: dotMatch[1] };

    const numberMatch = trimmedText.match(/^\d+$/);
    if (numberMatch) {
      return {
        type: numberMatch[0].length <= 7 ? 'mc' : 'dot',
        value: numberMatch[0]
      };
    }

    return { type: 'auto', value: trimmedText };
  }

  async search(term, searchType = 'auto', filters = {}) {
    const searchConfig = searchType === 'auto'
      ? this.determineSearchType(term)
      : { type: searchType, value: term };

   
    if (searchConfig.type === 'range') {
      if (!searchConfig.start || !searchConfig.end) {
        throw new Error('Please enter both start and end numbers for range search');
      }
      const start = parseInt(searchConfig.start, 10);
      const end = parseInt(searchConfig.end, 10);
      if (Number.isNaN(start) || Number.isNaN(end)) throw new Error('Please enter valid numbers for range search');
      if (start > end) throw new Error('Start number cannot be greater than end number');
      if (end - start > 50) throw new Error('Range too large. Please limit to 50 numbers maximum.');

      const rangeResults = await this.searchRange(start, end);
      return this.applyFilters(rangeResults, filters);
    }

    if (!tidy(term)) throw new Error('Please enter a search term');

 
    let params = {};
    switch (searchConfig.type) {
      case 'email':
        params = { email_address: searchConfig.value.toLowerCase() };
        break;
      case 'dot':
        params = { dot_number: stripLeadingZeros(digits(searchConfig.value)) };
        break;
      case 'mc': {
        const strippedMC = stripLeadingZeros(digits(searchConfig.value));
        params = { docket1: strippedMC };
        break;
      }
      case 'name':
      case 'auto': {
        const searchTerm = tidy(term).toLowerCase();

        const baseWhere =
         `lower(legal_name) like '%25${encodeURIComponent(searchTerm)}%25' OR ` +
          `lower(dba_name) like '%25${encodeURIComponent(searchTerm)}%25' OR ` +
         `dot_number='${searchTerm.replace(/'/g, "''")}' OR ` +
`docket1='${stripLeadingZeros(digits(searchTerm))}'`;

        const stArr = Array.isArray(filters.states) ? filters.states.filter(Boolean) : [];
 if (stArr.length) {
   const inList = stArr.map(s => `'${s.toUpperCase()}'`).join(',');
   params = {
     $where: `(${baseWhere}) AND (upper(phy_state) in (${inList}) OR upper(carrier_mailing_state) in (${inList}))`
  };
 } else {
   params = { $where: baseWhere };
 }
        break;
      }
      default:
        throw new Error('Invalid search type');
    }

    const data = await getJSONViaProxy(DS.BASIC, params);

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('No carrier found with the provided search criteria');
    }

    if (data.length > 1) {
      const processedData = data.map(item => this.processBasicCarrierData(item));
      return this.applyFilters(processedData, filters);
    }

    const basicData = this.processBasicCarrierData(data[0]);
    const mcNumber = basicData.mcNumber !== 'N/A' ? basicData.mcNumber : null;

    if (mcNumber) {
      try {
        const additionalData = await this.getAdditionalCarrierInfo(mcNumber); 
        const fullData = { ...basicData, ...additionalData };
        return this.applyFilters([fullData], filters);
      } catch {
        return this.applyFilters([basicData], filters);
      }
    }

    return this.applyFilters([basicData], filters);
  }

  async searchRange(start, end) {
    const promises = [];
    for (let i = start; i <= end; i++) {
      const delay = (i - start) * 100; // 100ms spacing
      promises.push(
        new Promise(resolve => setTimeout(resolve, delay))
          .then(() => this.searchSingleMC(i.toString()))
      );
    }

    const results = await Promise.allSettled(promises);
    const validResults = results
      .filter(r => r.status === 'fulfilled' && r.value !== null)
      .map(r => r.value);

    if (!validResults.length) throw new Error('No carriers found in the specified range');
    return validResults;
  }

  async searchSingleMC(mcNumber) {
    try {
      const strippedMC = stripLeadingZeros(digits(mcNumber));
      const params = { docket1: strippedMC };
      const data = await getJSONViaProxy(DS.BASIC, params);

      if (!Array.isArray(data) || data.length === 0) return null;
      return this.processBasicCarrierData(data[0]);
    } catch {
      return null;
    }
  }

  applyFilters(data, filters = {}) {
    if (!data) return null;
    let filteredData = Array.isArray(data) ? data : [data];

    if (filters.skipBrokers) {
      filteredData = filteredData.filter(carrier =>
        carrier.entityType && !carrier.entityType.includes('Broker')
      );
    }

    if (filters.skipInactive) {
      filteredData = filteredData.filter(carrier => carrier.status === 'Active');
    }

const stArr = Array.isArray(filters.states) ? filters.states.map(s => s.toUpperCase()) : [];
 if (stArr.length) {
   filteredData = filteredData.filter(c => {
     const p = (c.phyState || '').toUpperCase();
     const m = (c.mailState || '').toUpperCase();
     return stArr.includes(p) || stArr.includes(m) ||
       (c.physicalAddress && stArr.some(st => c.physicalAddress.includes(`, ${st} `))) ||
       (c.mailingAddress && stArr.some(st => c.mailingAddress.includes(`, ${st} `)));
   });
 }
  if (filters.showInactiveOnly) {
      filteredData = filteredData.filter(carrier => carrier.status !== 'Active');
    }

    if (filters.activeDuration > 0) {
      filteredData = filteredData.filter(carrier => {
        if (!carrier.activeDuration || carrier.activeDuration === 'N/A') return false;
        const monthsMatch = carrier.activeDuration.match(/(\d+)\s*months?/);
        const yearsMatch = carrier.activeDuration.match(/(\d+)\s*years?/);
        let totalMonths = 0;
        if (yearsMatch) totalMonths += parseInt(yearsMatch[1], 10) * 12;
        if (monthsMatch) totalMonths += parseInt(monthsMatch[1], 10);
        return totalMonths >= filters.activeDuration;
      });
    }

    if (filters.resultLimit && filteredData.length > filters.resultLimit) {
      filteredData = filteredData.slice(0, filters.resultLimit);
    }

    return filteredData.length === 1 ? filteredData[0] : filteredData;
  }

  async getAdditionalCarrierInfo(mcNumber) {
    const mc = normalizeMC(mcNumber);
    const endpoints = {
      carrierInfo: { ep: DS.CARRIER_INFO, params: { docket_number: mc } },
      insuranceInfo: { ep: DS.INSURANCE, params: { prefix_docket_number: mc } },
      pendingInsurance: { ep: DS.PENDING_INS, params: { docket_number: mc } },
      authorityHistory: { ep: DS.AUTH_HISTORY, params: { docket_number: mc } },
      revocationOrders: { ep: DS.REVOCATIONS, params: { docket_number: mc } }
    };

    const promises = Object.entries(endpoints).map(async ([key, { ep, params }]) => {
      try {
        const arr = await getJSONViaProxy(ep, params);
        const data = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
        return { key, data };
      } catch (error) {
        return { key, data: null, error: error.message };
      }
    });

    const results = await Promise.all(promises);
    const additionalData = {};

    results.forEach(({ key, data, error }) => {
      if (data && !error) {
        switch (key) {
          case 'carrierInfo':
            additionalData.authorityInfo = this.processAuthorityData(data);
            additionalData.alerts = this.collectAlerts([{ title: 'Carrier Info', data: [data] }]);
            break;
          case 'insuranceInfo':
            additionalData.insuranceInfo = this.processInsuranceData(data);
            break;
          case 'pendingInsurance':
            additionalData.pendingInsurance = this.processPendingInsuranceData(data);
            break;
          case 'authorityHistory':
            additionalData.authorityHistory = this.processAuthorityHistory(data);
            additionalData.activeDuration = this.calculateActiveDuration(data);
            break;
          case 'revocationOrders':
            additionalData.revocationInfo = this.processRevocationData(data);
            break;
          default:
            break;
        }
      }
    });

    return additionalData;
  }

  
  processBasicCarrierData(carrier) {
    const carshipMap = {
      'C': 'Carrier', 'S': 'Shipper', 'B': 'Broker', 'R': 'Registrant',
      'F': 'Freight Forwarder', 'I': 'Intermodal Equipment Provider', 'T': 'Cargo Tank'
    };

    const cargoClassifications = {
      'crgo_genfreight': 'General Freight', 'crgo_household': 'Household Goods',
      'crgo_metalsheet': 'Metal: Sheets, Coils, Rolls', 'crgo_motoveh': 'Motor Vehicles',
      'crgo_drivetow': 'Driveaway/Towaway', 'crgo_logpole': 'Logs, Poles, Beams, Lumber',
      'crgo_bldgmat': 'Building Materials', 'crgo_mobilehome': 'Mobile Homes',
      'crgo_machlrg': 'Machinery, Large Objects', 'crgo_produce': 'Fresh Produce',
      'crgo_liqgas': 'Liquids/Gases', 'crgo_intermodal': 'Intermodal Containers',
      'crgo_passengers': 'Passengers', 'crgo_oilfield': 'Oilfield Equipment',
      'crgo_livestock': 'Livestock', 'crgo_grainfeed': 'Grain, Feed, Hay',
      'crgo_coalcoke': 'Coal, Coke', 'crgo_meat': 'Meat',
      'crgo_garbage': 'Garbage, Refuse, Trash', 'crgo_usmail': 'U.S. Mail',
      'crgo_chem': 'Chemicals', 'crgo_drybulk': 'Commodities Dry Bulk',
      'crgo_coldfood': 'Refrigerated Food', 'crgo_beverages': 'Beverages',
      'crgo_paperprod': 'Paper Products', 'crgo_utility': 'Utility',
      'crgo_farmsupp': 'Farm Supplies', 'crgo_construct': 'Construction',
      'crgo_waterwell': 'Water - Well', 'crgo_cargoothr': 'Other'
    };

    const cargoTypes = Object.keys(cargoClassifications)
      .filter(key => carrier[key] === 'X')
      .map(key => cargoClassifications[key]);

    const statusMap = { 'A': 'Active', 'I': 'Inactive' };
    const carrierOperationMap = {
      'A': 'Interstate', 'B': 'Intrastate Hazardous Material', 'C': 'Intrastate Non-Hazardous Material'
    };

    const businessOrgMap = { '1': 'Individual', '2': 'Partnership', '3': 'Corporation' };

    const entityTypes = carrier.carship
      ? carrier.carship.split(';').map(type => carshipMap[type] || 'Unknown').join(', ')
      : 'N/A';

    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6) - 1;
      const day = dateStr.substring(6, 8);
      return new Date(year, month, day).toLocaleDateString('en-US');
    };

    return {
   
      legalName: carrier.legal_name || 'N/A',
      dbaName: carrier.dba_name || 'N/A',
      dotNumber: carrier.dot_number || 'N/A',
      mcNumber: carrier.docket1 || 'N/A',
      phyState: carrier.phy_state || 'N/A',
      mailState: carrier.carrier_mailing_state || 'N/A',
      entityType: entityTypes,
      entityClassification: carrier.classdef || 'N/A',
      status: statusMap[carrier.status_code] || 'N/A',
      carrierOperation: carrierOperationMap[carrier.carrier_operation] || 'N/A',
      businessOrganization: businessOrgMap[carrier.business_org_id] || 'N/A',

    
      cargoClassification: cargoTypes.length > 0 ? cargoTypes.join(', ') : 'N/A',
      mcs150Date: formatDate(carrier.mcs150_date),
      addDate: formatDate(carrier.add_date),
      mcs150Mileage: `${carrier.mcs150_mileage || 'N/A'} miles`,
      mcs150MileageYear: carrier.mcs150_mileage_year || 'N/A',

  
      companyOfficer1: carrier.company_officer_1 || 'N/A',
      companyOfficer2: carrier.company_officer_2 || 'N/A',
      phone: carrier.phone || 'N/A',
      fax: carrier.fax || 'N/A',
      cellPhone: carrier.cell_phone || 'N/A',
      email: carrier.email_address || 'N/A',
      physicalAddress: `${carrier.phy_street || 'N/A'}, ${carrier.phy_city || 'N/A'}, ${carrier.phy_state || 'N/A'} ${carrier.phy_zip || 'N/A'}`,
      mailingAddress: `${carrier.carrier_mailing_street || 'N/A'}, ${carrier.carrier_mailing_city || 'N/A'}, ${carrier.carrier_mailing_state || 'N/A'} ${carrier.carrier_mailing_zip || 'N/A'}`,

      totalCDLDrivers: carrier.total_cdl || '0',
      totalDrivers: carrier.total_drivers || '0',
      powerUnits: carrier.power_units || '0',
      ownTrucks: carrier.owntruck || '0',
      ownTractors: carrier.owntract || '0',
      ownTrailers: carrier.owntrail || '0',
      termLeasedTrucks: carrier.trmtruck || '0',
      termLeasedTractors: carrier.trmtract || '0',
      termLeasedTrailers: carrier.trmtrail || '0',

      
      authorityInfo: null,
      insuranceInfo: null,
      pendingInsurance: null,
      authorityHistory: null,
      revocationInfo: null,
      activeDuration: null,
      alerts: []
    };
  }

  processAuthorityData(data) {
    if (!data) return null;
    return {
      commonAuthority: data.common_stat === 'A' ? 'Active' : data.common_stat === 'I' ? 'Inactive' : 'No Authority',
      brokerAuthority: data.broker_stat === 'A' ? 'Active' : data.broker_stat === 'I' ? 'Inactive' : 'No Authority',
      pendingCommon: data.common_app_pend === 'Y',
      pendingContract: data.contract_app_pend === 'Y',
      pendingBroker: data.broker_app_pend === 'Y',
      revocationCommon: data.common_rev_pend === 'Y',
      revocationContract: data.contract_rev_pend === 'Y',
      revocationBroker: data.broker_rev_pend === 'Y'
    };
  }

  processInsuranceData(data) {
    if (!data) return null;

    const insTypeMapping = {
      "1.0": "BI&PD", "2.0": "Cargo", "3.0": "Bond", "4.0": "Trust Fund"
    };

    const insClassMapping = {
      "P": "Primary", "E": "Excess",
      "1": "Full Security Limits Under Section 1043.2(b)(1)",
      "2": "Full Security Limits Under Section 1043.2(b)(2)"
    };

    const insFormMapping = {
      "34": "Cargo", "82": "BI&PD", "83": "Cargo", "84": "Property Broker's Surety Bond",
      "85": "Property Broker's Trust Fund Agreement", "91": "BI&PD/Primary, BI&PD",
      "91X": "BI&PD, BI&PD/Primary, BI&PD/Excess"
    };

    return {
      type: insTypeMapping[data.ins_type_code] || 'Unknown',
      class: insClassMapping[data.ins_class_code] || 'Unknown',
      maxCoverage: data.max_cov_amount ? `$${(parseFloat(data.max_cov_amount) * 1000).toLocaleString()}` : 'N/A',
      underlyingLimit: data.underl_lim_amount ? `$${(parseFloat(data.underl_lim_amount) * 1000).toLocaleString()}` : 'N/A',
      policyNumber: data.policy_no || 'N/A',
      effectiveDate: data.effective_date || 'N/A',
      formCode: insFormMapping[data.ins_form_code] || 'Unknown',
      companyName: data.name_company || 'N/A'
    };
  }

  processPendingInsuranceData(data) {
    if (!data || !data.cancl_effective_date) return null;
    return {
      formCode: data.ins_form_code || 'N/A',
      typeDescription: data.mod_col_1 || 'N/A',
      companyName: data.name_company || 'N/A',
      policyNumber: data.policy_no || 'N/A',
      postedDate: data.trans_date || 'N/A',
      underlyingLimit: data.underl_lim_amount ? `$${(parseFloat(data.underl_lim_amount) * 1000).toLocaleString()}` : 'N/A',
      maxCoverage: data.max_cov_amount ? `$${(parseFloat(data.max_cov_amount) * 1000).toLocaleString()}` : 'N/A',
      effectiveDate: data.effective_date || 'N/A',
      cancelEffectiveDate: data.cancl_effective_date || 'N/A'
    };
  }

  processAuthorityHistory(data) {
    if (!data) return null;
    return {
      action: data.original_action_desc || 'N/A',
      serviceType: data.mod_col_1 || 'N/A',
      servedDate: data.disp_served_date || data.orig_served_date || 'N/A'
    };
  }

  processRevocationData(data) {
    if (!data) return null;
    return {
      hasRevocation: data.common_rev_pend === 'Y'
    };
  }

  calculateActiveDuration(data) {
    if (!data) return 'N/A';
    try {
      const grantedEntry = Array.isArray(data)
        ? data.find(event => event.mod_col_1 === "MOTOR PROPERTY COMMON CARRIER" && event.original_action_desc === "GRANTED")
        : data;

      if (grantedEntry) {
        const grantedDate = new Date(grantedEntry.disp_served_date || grantedEntry.orig_served_date);
        const currentDate = new Date();
        const duration = currentDate - grantedDate;

        const years = Math.floor(duration / (1000 * 60 * 60 * 24 * 365));
        const months = Math.floor((duration % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));

        if (years === 0) return `${months} months`;
        if (months === 0) return `${years} years`;
        return `${years} years, ${months} months`;
      }
    } catch {/* ignore */}
    return 'N/A';
  }

  collectAlerts(results) {
    const alerts = [];
    (results || []).forEach(({ data }) => {
      if (data && data.length > 0) {
        const item = data[0];
        if (item.common_rev_pend === 'Y') alerts.push("Common Authority In Revocation");
        if (item.cancl_effective_date) alerts.push(`Pending insurance cancellation effective: ${item.cancl_effective_date}`);
      }
    });
    return alerts;
  }
}

const apiService = new CarrierAPIService();


export const useCarrierData = () => {
  const { isAuthenticated, recordSearch, usageRemaining } = useAuth();
  const [carrierData, setCarrierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBasicInfo, setShowBasicInfo] = useState(true);

  const ensureSearchAllowance = useCallback(async (term, type) => {
    if (!isAuthenticated) {
      throw new Error('Please log in to perform searches');
    }
    if (recordSearch) {
      await recordSearch(term, type);
    }
  }, [isAuthenticated, recordSearch]);

  const searchCarrierData = useCallback(async (searchTerm, searchType = 'auto', filters = {}) => {
    setLoading(true);
    setError(null);
    setCarrierData(null);
    setShowBasicInfo(true);

    try {
      const config = searchType === 'auto'
        ? apiService.determineSearchType(searchTerm)
        : { type: searchType, value: searchTerm };
      const typeForLog = (config && config.type) || searchType || 'auto';
      const valueForLog =
        (config && config.value) || searchTerm;
      await ensureSearchAllowance(valueForLog, typeForLog);
      const data = await apiService.search(searchTerm, searchType, filters);
      setCarrierData(data);
    } catch (err) {
      setError(err.message || "An error occurred while searching");
    } finally {
      setLoading(false);
    }
  }, [ensureSearchAllowance]);

  const clearBatchResults = useCallback(() => {
    setCarrierData(null);
    setError(null);
    setShowBasicInfo(true);
    setLoading(false);
  }, []);

  const batchSearch = useCallback(async (batchInput, filters = {}) => {
    setLoading(true);
    setError('');
    try {
      const searchTerms = batchInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (searchTerms.length === 0) throw new Error('Please enter at least one search term');

      let existing = null;
      setCarrierData(prev => {
        existing = prev && prev.searchType === 'batch' ? prev : null;
        return {
          searchType: 'batch',
          totalSearched: (existing ? existing.totalSearched : 0) + searchTerms.length,
          processed: existing ? existing.processed : 0,
          successful: existing ? existing.successful : 0,
          errors: existing ? [...(existing.errors || [])] : [],
          batchResults: existing ? [...(existing.batchResults || [])] : [],
          isComplete: false
        };
      });

      for (let i = 0; i < searchTerms.length; i++) {
        const term = searchTerms[i];
        try {
          const detected = apiService.determineSearchType(term);
          let searchTypeToUse = 'auto';
          let searchValue = term;

          if (detected && typeof detected === 'object' && detected.type) {
            if (detected.type === 'range') {
              searchTypeToUse = 'auto'; // let service parse start/end
              searchValue = term;
            } else if (['mc', 'dot', 'email', 'name'].includes(detected.type)) {
              searchTypeToUse = detected.type;
              searchValue = detected.value || term;
            }
          }

          await ensureSearchAllowance(searchValue, searchTypeToUse);
          const result = await apiService.search(searchValue, searchTypeToUse, filters);

          if (result) {
            setCarrierData(prev => {
              const prevState = prev || {
                searchType: 'batch',
                totalSearched: searchTerms.length,
                processed: 0,
                successful: 0,
                errors: [],
                batchResults: [],
                isComplete: false
              };
              return {
                ...prevState,
                processed: (prevState.processed || 0) + 1,
                successful: (prevState.successful || 0) + 1,
                batchResults: [
                  ...(prevState.batchResults || []),
                  ...(Array.isArray(result) ? result : [result])
                ]
              };
            });
          } else {
            setCarrierData(prev => ({
              ...prev,
              processed: (prev.processed || 0) + 1
            }));
          }
        } catch (err) {
          setCarrierData(prev => ({
            ...prev,
            processed: (prev.processed || 0) + 1,
            errors: [
              ...(prev.errors || []),
              { term, error: err.message }
            ]
          }));
        }
     
        await sleep(50);
      }

      setCarrierData(prev => ({ ...prev, isComplete: true }));
    } catch (err) {
      setError(err.message || 'Batch search failed');
      setCarrierData(null);
    } finally {
      setLoading(false);
    }
  }, [ensureSearchAllowance]);

  const toggleInfoView = useCallback(() => {
    setShowBasicInfo(prev => !prev);
  }, []);

  return {
    carrierData,
    loading,
    error,
    searchCarrier: searchCarrierData,
    batchSearch,
    clearBatchResults,
    showBasicInfo,
    toggleInfoView,
    usageRemaining
  };
};
